import React, { useEffect, useState, useCallback } from 'react';
import { PayPalButtons } from "@paypal/react-paypal-js";
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Box, Typography } from '@mui/material';
import { UPDATE_SUBSCRIPTION } from '../../graphql/mutations/subscription';
import { SEND_EMAIL_MUTATION } from '../../graphql/mutations';

export async function updateDowngradeSubscription({
    token,
    selectedPlanId,
    currentPlan,
    paypalPlanId,
    plan,
    updateSubscription, // a mutation function
    sendSubscriptionEmail, // a mutation function
    navigate, // navigation function (e.g., from react-router)
}) {
    try {
        // Update subscription with pending downgrade info.
        const newTokenResponse = await updateSubscription({
            variables: {
                input: {
                    userId: token.userId,
                    subscriptionId: Number(token.subscriptionId),
                    selectedPlanId: Number(selectedPlanId),
                    // Set pending fields for the downgrade. 
                    pendingPaypalPlanId: paypalPlanId,
                    staffIdSubscibeForBusiness: Number(token.staffId),
                },
            },
        });
        localStorage.setItem("token", newTokenResponse.data.updateSubscription);
        navigate(`/biz/subscription-success/${plan.name}`);
    } catch (error) {
        console.error("Error updating subscription:", error);
        alert("An error occurred while updating your subscription.");
        return;
    }

    // Prepare email notification.
    const emailSubject = "Subscription Downgrade Scheduled";
    const emailBody = `Hello,
  
                        Your current ${currentPlan.name} plan will remain active until the end of the current billing cycle.
                        A downgrade to ${plan.name} plan is scheduled, and your new payment will start with the next cycle.

                        Thank you,
                        Your Team`;

    try {
        await sendSubscriptionEmail({
            variables: {
                to: token.email,
                subject: emailSubject,
                text: emailBody,
            },
        });
    } catch (emailError) {
        console.error("Error sending subscription email:", emailError);
    }
}


export const PaypalPlanId = (plan, duration) => {
    if (!plan.paymentMethods || plan.paymentMethods.length === 0) {
        return null;
    }

    // Filter valid payment methods by active status and name 'Paypal'
    const validPaymentMethods = plan.paymentMethods.filter(p => {
        return p.isActive && p.paymentName.toLowerCase() === 'paypal';
    });


    if (validPaymentMethods.length === 0) {
        return null;
    }

    // Find the correct payment method for the current plan
    const correctPaymentMethod = validPaymentMethods.find(p => String(p.planId) === String(plan.id)) || validPaymentMethods[0];

    const planKey = duration === "Yearly" ? 'yearlyPlanId' : 'monthlyPlanId';
    const selectedPayPalPlanId = correctPaymentMethod[planKey]; 
    return selectedPayPalPlanId || null;
};

const SubscriptionButton = ({ plans, selectedPlanId, duration, token }) => {

    const plan = plans?.find(
        (plan) => Number(plan.id) === Number(selectedPlanId)
    )
    const currentPlanId = Number(token.planId)

    const currentPlan = plans?.find(
        (plan) => Number(plan.id) === currentPlanId
    )
    const [isOpen, setIsOpen] = useState(false);
    const [sendSubscriptionEmail] = useMutation(SEND_EMAIL_MUTATION);

    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);

    const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION);
    const navigate = useNavigate();

    const getPaypalPlanId = useCallback(() => {
        return PaypalPlanId(plan, duration)
    }, [plan, duration]);

    const [paypalPlanId, setPaypalPlanId] = useState(getPaypalPlanId());

    useEffect(() => {
        const newPlanId = getPaypalPlanId();

        // Only update the state if the new paypalPlanId is different
        if (newPlanId !== paypalPlanId) {
            setPaypalPlanId(newPlanId);
        }
    }, [duration, plan]); 


    const isFree = Number(plan.id) === 1;

    const createSubscription = useCallback(async (data, actions) => {
        if (selectedPlanId < currentPlanId) {
            // Schedule the downgrade instead of cancelling immediately.
            // Retain the current plan until the downgrade takes effect.
            // Set pending fields to store the selected (new) plan.
            try {
            await updateDowngradeSubscription({
                token,
                selectedPlanId,
                currentPlan,
                paypalPlanId,
                plan,
                updateSubscription, // mutation function
                sendSubscriptionEmail, // mutation function
                navigate,
            });
            return token.subscriptionID;
          } catch (error) {
              console.error("Error updating downgrade subscription: ", error);
              throw error;
          }
        } else {
            // For upgrades, create a new subscription on PayPal.
            try {
                const subscriptionID = await actions.subscription.create({
                    plan_id: paypalPlanId,
                    quantity: 1,
            });
              return subscriptionID;
          } catch (error) {
              console.error("Error creating PayPal subscription: ", error);
              throw error;
          }
        }
    }, [
        selectedPlanId,
        currentPlanId,
        token,
        currentPlan,
        paypalPlanId,
        plan,
        updateSubscription,
        sendSubscriptionEmail,
        navigate,
    ]);


    const onApprove = async (data, actions) => {
        // upgrade is immidate while downgrade is later done at the backend using job
        // Get the new subscription ID from PayPal (for upgrades) or use the existing one (for downgrades)
        const newSubscriptionID = data.subscriptionID;
        const selectedPlanId = Number(plan.id);
        const currentPlanId = Number(token.planId);

        // Upgrade flow: when the selected plan is higher than the current one.
        if (selectedPlanId > currentPlanId) {
            try { 
                const newTokenResponse = await updateSubscription({
                    variables: {
                        input: {
                            userId: token.userId,
                            subscriptionId: Number(token.subscriptionId),
                            subscriptionID: newSubscriptionID, // New subscription ID from PayPal.
                            selectedPlanId: selectedPlanId, // Immediately apply the upgrade. 
                            subscribedOnDate: new Date().toISOString(),
                            businessId: token.defaultBusinessId,
                            staffIdSubscibeForBusiness: Number(token.staffId),
                        },
                    },
                });
                localStorage.setItem("token", newTokenResponse.data.updateSubscription);
                // Now send the email after updating the subscription.
                const emailSubject = 'Subscription Upgrade Successful';
                const emailBody =
                `Hello,

                Your subscription has been upgraded to the ${plan.name} plan. The new payment will start at the beginning of your next billing cycle.
                    
                Thank you,
                Your Team`;

                await sendSubscriptionEmail({
                    variables: {
                        to: token.email,
                        subject: emailSubject,
                        text: emailBody,
                    },
                });
                navigate(`/biz/subscription-success/${plan.name}`);
            } catch (error) {
                console.error("Error during upgrade process:", error);
                alert("An error occurred while processing your upgrade.");
                return;
            } 
        } else {
            // If no plan change, do nothing.
            console.log("No subscription change requested.");
        }
    }; 

    return (
        <>
            <Button variant="contained" color="secondary" onClick={openModal}>
                {token.planId === 1 ? "Choose Plan" : token.planId === plan.id ? "Current Plan" : "Choose Plan"}
            </Button>

            <Modal
                open={isOpen}
                onClose={closeModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                }}>
                    <Typography id="modal-title" variant="h6" component="h2">
                        {plan.name} Plan
                    </Typography>
                    <Typography id="modal-description" sx={{ mt: 2 }}>
                        Duration: {duration}
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        Price: ${duration === "Yearly" ? plan.priceYearly : plan.priceMonthly}
                    </Typography>
                    {plan.paymentMethods.map((paymentMethod) => {
                        if (paymentMethod.paymentName.toLowerCase() === 'paypal') {
                            return <Box sx={{ mt: 2 }}>
                                <PayPalButtons
                                    key={paypalPlanId}
                                    style={{
                                        shape: 'rect',
                                        color: 'gold',
                                        layout: 'vertical',
                                        label: 'subscribe'
                                    }}
                                    createSubscription={createSubscription}
                                    onApprove={onApprove}
                                />
                            </Box>
                        } else if (paymentMethod.paymentName === 'Stripe') {
                            return <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                {/* center the button */}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        // Add your Stripe payment logic here
                                        console.log('Stripe payment logic');
                                    }}
                                    disabled={!paymentMethod.isActive}
                                >
                                    Subscribe with Stripe
                                </Button>
                            </Box>
                        }
                    })}

                </Box>
            </Modal>
        </>
    );
};

export default SubscriptionButton;
